import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService } from '../../../services/company.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';

interface CompanyProfile {
    company: any;
    corporateEnrollments: any[];
    financialSummary: any;
}

@Component({
    selector: 'app-profile-company',
    templateUrl: './profile-company.component.html',
    styleUrl: './profile-company.component.scss'
})
export class ProfileCompanyComponent implements OnInit {

    companyId: number = 0;
    profile: CompanyProfile | null = null;
    activeTab: string = 'overview';

    // Estados de carga
    isLoading: boolean = false;

    // Inscripción expandida
    expandedEnrollmentId: number | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private companyService: CompanyService,
        private loadingService: LoadingService,
        private toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            if (id) {
                this.companyId = +id;
                this.loadCompanyProfile();
            } else {
                this.toastService.showToast('error', 'ID de la empresa no válido');
                this.router.navigate(['/companies']);
            }
        });
    }

    private loadCompanyProfile(): void {
        if (this.companyId <= 0) return;

        this.isLoading = true;
        this.loadingService.show();

        this.companyService.getProfile(this.companyId).subscribe({
            next: (response) => {
                console.log('🏢 Profile response:', response);
                this.profile = response;
                this.isLoading = false;
                this.loadingService.hide();
            },
            error: (error) => {
                console.error('❌ Error loading company profile:', error);
                this.toastService.showToast('error', 'Error al cargar el perfil de la empresa');
                this.isLoading = false;
                this.loadingService.hide();
            }
        });
    }

    setActiveTab(tab: string): void {
        this.activeTab = tab;
    }

    toggleEnrollment(enrollmentId: number): void {
        this.expandedEnrollmentId = this.expandedEnrollmentId === enrollmentId ? null : enrollmentId;
    }

    isEnrollmentExpanded(enrollmentId: number): boolean {
        return this.expandedEnrollmentId === enrollmentId;
    }

    getStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active': return 'active';
            case 'completed': return 'completed';
            case 'withdrawn': return 'withdrawn';
            case 'deferred': return 'deferred';
            case 'slottransfer': return 'slottransfer';
            default: return 'active';
        }
    }

    getStatusLabel(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active': return 'Activo';
            case 'completed': return 'Completado';
            case 'withdrawn': return 'Retirado';
            case 'deferred': return 'Aplazado';
            case 'slottransfer': return 'Cesión de Cupo';
            default: return status || 'Activo';
        }
    }

    getPaymentStatusClass(status: number): string {
        switch (status) {
            case 1: return 'pending'; // PENDING
            case 2: return 'paid'; // PAID
            case 3: return 'overdue'; // OVERDUE
            case 4: return 'partial'; // PARTIALLY_PAID
            case 5: return 'link-generated'; // LINK_GENERATED
            default: return 'pending';
        }
    }

    getCoverageTypeLabel(type: number): string {
        switch (type) {
            case 0: return 'Sin cobertura';
            case 1: return 'Porcentaje';
            case 2: return 'Monto fijo';
            default: return 'Desconocido';
        }
    }

    goBack(): void {
        this.router.navigate(['/companies']);
    }

    editCompany(): void {
        this.router.navigate(['/companies/view', this.companyId]);
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);
    }

    /**
     * Obtiene lista de participantes únicos de todas las inscripciones
     */
    getUniqueParticipants(): any[] {
        if (!this.profile?.corporateEnrollments) return [];

        const allParticipants: any[] = [];
        this.profile.corporateEnrollments.forEach(enrollment => {
            if (enrollment.participants) {
                enrollment.participants.forEach((p: any) => {
                    allParticipants.push({
                        ...p,
                        projectName: enrollment.projectName
                    });
                });
            }
        });
        return allParticipants;
    }

    formatDate(date: string | Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
    }
}
